// src/components/Layout.jsx
//
// Professional, collapsible app shell with a locked light/dark theme and a
// global top bar.
//
//  • Global top bar: search box (→ /search) + a colour-coded ROLE badge that
//    shows whether a Superadmin, Admin or User opened the panel, plus a quick
//    theme toggle. On phones it also carries the drawer (burger) button.
//  • Collapse toggle in the sidebar head shrinks the rail to icons only, with a
//    floating label on hover.
//  • Profile + sign-out live in ONE account button that opens a popover.
//  • Every colour is a CSS variable, so the theme toggle drives the whole shell.
import { useEffect, useMemo, useRef, useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/auth.jsx'
import { useTheme } from '../context/theme.jsx'
import { TIMEZONES, useTimezone } from '../context/timezone.jsx'
import { useBranding } from '../context/branding.jsx'

const COLLAPSE_KEY = 'clixtrack_sidebar_collapsed'

const NAV = [
  { section: 'REPORTING', items: [
    { to: '/superadmin', icon: 'dashboard', label: 'Dashboard' },
    { to: '/panel-offers', icon: 'list', label: 'Offers Report' },
    { to: '/panel-users', icon: 'person', label: 'Users Report' },
    { to: '/panel-clicks', icon: 'users', label: 'Clicks Report' },
    { to: '/panel-conversions', icon: 'target', label: 'Conversions' },
  ]},
  { section: 'SYSTEM', items: [
    { to: '/sources', icon: 'globe', label: 'Data Sources' },
    { to: '/panel-settings', icon: 'key', label: 'Settings' },
    { to: '/audit', icon: 'report', label: 'Activity Log' },
  ]},
]

const SVGS = {
  dashboard:    <><rect x="3" y="3" width="7.5" height="7.5" rx="1.6"/><rect x="13.5" y="3" width="7.5" height="7.5" rx="1.6"/><rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.6"/><rect x="3" y="13.5" width="7.5" height="7.5" rx="1.6"/></>,
  plus:         <><circle cx="12" cy="12" r="9"/><line x1="12" y1="8.5" x2="12" y2="15.5"/><line x1="8.5" y1="12" x2="15.5" y2="12"/></>,
  list:         <><line x1="8" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="20" y2="12"/><line x1="8" y1="18" x2="20" y2="18"/><circle cx="4" cy="6" r="1"/><circle cx="4" cy="12" r="1"/><circle cx="4" cy="18" r="1"/></>,
  share:        <><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.6" y1="10.6" x2="15.4" y2="6.4"/><line x1="8.6" y1="13.4" x2="15.4" y2="17.6"/></>,
  bar:          <><line x1="6" y1="20" x2="6" y2="13"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="18" y1="20" x2="18" y2="9"/></>,
  report:       <><path d="M4 3h11l5 5v13a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z"/><polyline points="14 3 14 9 20 9"/><line x1="8" y1="14" x2="16" y2="14"/><line x1="8" y1="18" x2="13" y2="18"/></>,
  users:        <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>,
  network:      <><circle cx="18" cy="5" r="2.4"/><circle cx="6" cy="12" r="2.4"/><circle cx="18" cy="19" r="2.4"/><line x1="8" y1="13.2" x2="16" y2="17.8"/><line x1="16" y1="6.2" x2="8" y2="10.8"/></>,
  code:         <><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></>,
  person:       <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></>,
  globe:        <><circle cx="12" cy="12" r="9"/><line x1="3" y1="12" x2="21" y2="12"/><path d="M12 3a15 15 0 0 1 4 9 15 15 0 0 1-4 9 15 15 0 0 1-4-9 15 15 0 0 1 4-9z"/></>,
  key:          <><circle cx="7.5" cy="15.5" r="4.5"/><line x1="10.7" y1="12.3" x2="20" y2="3"/><line x1="17" y1="6" x2="20" y2="9"/><line x1="15" y1="8" x2="18" y2="11"/></>,
  sun:          <><circle cx="12" cy="12" r="4"/><line x1="12" y1="2" x2="12" y2="4"/><line x1="12" y1="20" x2="12" y2="22"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="2" y1="12" x2="4" y2="12"/><line x1="20" y1="12" x2="22" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></>,
  moon:         <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>,
  menu:         <><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></>,
  logout:       <><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></>,
  search:       <><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></>,
  x:            <><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>,
  expand:       <><polyline points="13 17 18 12 13 7"/><polyline points="6 17 11 12 6 7"/></>,
  caret:        <polyline points="6 9 12 15 18 9"/>,
  link:         <><path d="M9 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1.5 1.5"/><path d="M15 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1.5-1.5"/></>,
  target:       <><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5"/><circle cx="12" cy="12" r="1.5"/></>,
  trash:        <><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></>,
}

function Ico({ name, size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      {SVGS[name]}
    </svg>
  )
}

// The supplied Affisee A icon is split into two theme-aware masks. The A uses
// the current sidebar text colour, while its border/swoosh use the active
// theme accent colour.
function LogoMark() {
  return (
    <span className="ct-affisee-icon" aria-hidden="true">
      <span className="ct-affisee-icon-layer ct-affisee-icon-white" />
      <span className="ct-affisee-icon-layer ct-affisee-icon-accent" />
    </span>
  )
}

const ROLE_LABELS = { superadmin: 'Super Admin', admin: 'Admin', user: 'User' }

// Page names shown in the top bar (next to / in place of the old greeting).
const PAGE_TITLES = {
  '/': 'Central Dashboard',
  '/superadmin': 'Central Dashboard',
  '/panel-offers': 'Offers Report',
  '/panel-users': 'Users Report',
  '/panel-clicks': 'Clicks Report',
  '/panel-conversions': 'Conversions Report',
  '/sources': 'Data Sources',
  '/panel-settings': 'Settings',
  '/audit': 'Activity Log',
  '/profile': 'Profile',
}

function RoleBadge({ role }) {
  const key = (role || 'user').toLowerCase()
  const cls = ['superadmin', 'admin', 'user'].includes(key) ? key : 'user'
  return (
    <span className={`ct-role role-${cls}`} title={`Signed in as ${ROLE_LABELS[cls]}`}>
      <span className="ct-role-dot" />
      <span>{ROLE_LABELS[cls]}</span>
      <span className="ct-role-sub ct-hide-sm">Panel</span>
    </span>
  )
}


function TimezoneSelect() {
  const { timezone, setTimezone } = useTimezone()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const rootRef = useRef(null)
  const selectedRef = useRef(null)

  const selected = TIMEZONES.find((item) => item.value === timezone) || {
    value: timezone,
    label: timezone,
  }
  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return TIMEZONES
    return TIMEZONES.filter((item) => `${item.label} ${item.value}`.toLowerCase().includes(query))
  }, [search])

  useEffect(() => {
    if (!open) return undefined
    const close = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false)
    }
    const escape = (event) => { if (event.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', close)
    document.addEventListener('keydown', escape)
    const timer = window.setTimeout(() => selectedRef.current?.scrollIntoView({ block: 'center' }), 0)
    return () => {
      document.removeEventListener('mousedown', close)
      document.removeEventListener('keydown', escape)
      window.clearTimeout(timer)
    }
  }, [open])

  const choose = (value) => {
    setTimezone(value)
    setOpen(false)
    setSearch('')
  }

  return (
    <div className="ct-timezone-select" ref={rootRef}>
      <button
        type="button"
        className="ct-timezone-trigger"
        onClick={() => { setOpen((value) => !value); setSearch('') }}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={selected.label}
      >
        <Ico name="globe" size={16} />
        <span>{selected.label}</span>
        <Ico name="caret" size={15} />
      </button>

      {open && (
        <div className="ct-timezone-menu">
          <label className="ct-timezone-search">
            <Ico name="search" size={17} />
            <input
              autoFocus
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search timezone..."
              aria-label="Search timezone"
            />
          </label>
          <div className="ct-timezone-options" role="listbox" aria-label="Timezones">
            {filtered.length === 0 ? (
              <div className="ct-timezone-empty">No timezone found</div>
            ) : filtered.map((item) => {
              const active = item.value === timezone
              return (
                <button
                  type="button"
                  key={item.value}
                  ref={active ? selectedRef : undefined}
                  className={'ct-timezone-option' + (active ? ' active' : '')}
                  onClick={() => choose(item.value)}
                  role="option"
                  aria-selected={active}
                >
                  <span className="ct-timezone-check">{active ? '✓' : ''}</span>
                  <span>{item.label}</span>
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

function initials(name) {
  if (!name) return 'U'
  const p = name.trim().split(/\s+/)
  return ((p[0]?.[0] || '') + (p[1]?.[0] || '')).toUpperCase() || 'U'
}

export function TopbarActions({ children }) {
  return <div className="ct-inline-page-actions">{children}</div>
}

export default function Layout({ children }) {
  const { user, logout } = useAuth()
  const { logo_url: logoUrl, favicon_url: faviconUrl, logo_mode: logoMode } = useBranding()
  const siteName = 'Panelz'
  const { theme, preset, cyclePreset } = useTheme()
  const isUser = user?.role === 'user'
  const location = useLocation()
  const navigate = useNavigate()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(COLLAPSE_KEY) === '1',
  )
  const [tip, setTip] = useState(null)
  const [q, setQ] = useState('')
  const footRef = useRef(null)
  const pageTitle = PAGE_TITLES[location.pathname] || PAGE_TITLES['/' + location.pathname.split('/')[1]] || 'Dashboard'

  useEffect(() => {
    localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0')
  }, [collapsed])

  useEffect(() => { setMobileOpen(false) }, [location.pathname])

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { setMobileOpen(false) }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const allowed = (roles) => !roles || roles.includes(user?.role)
  const dashboardPath = '/superadmin'

  const showTip = (e, label) => {
    if (!collapsed) return
    const r = e.currentTarget.getBoundingClientRect()
    setTip({ label, y: r.top + r.height / 2 })
  }
  const hideTip = () => setTip(null)

  const runSearch = (e) => {
    e.preventDefault()
    const term = q.trim()
    if (term) navigate('/search?q=' + encodeURIComponent(term))
  }

  return (
    <>
    <div className={'ct-shell' + (collapsed ? ' is-collapsed' : '')}>

      {mobileOpen && <div className="ct-overlay" onClick={() => setMobileOpen(false)} />}

      {collapsed && tip && (
        <div className="ct-tip" style={{ top: tip.y }}>{tip.label}</div>
      )}

      {/* ════════════ SIDEBAR ════════════ */}
      <aside className={'ct-sidebar' + (mobileOpen ? ' is-open' : '')}>
        <div className="ct-side-head">
          <button
            type="button"
            className="ct-brand ct-affisee-lockup"
            onClick={() => {
              const mobileViewport = typeof window !== 'undefined' && window.matchMedia('(max-width: 900px)').matches
              if (mobileViewport) setMobileOpen(false)
              else setCollapsed((v) => !v)
              setTip(null)
            }}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Show full menu' : 'Hide menu'}
          >
            {logoMode === 'custom' ? <>
              <span
                className="ct-custom-theme-logo ct-custom-sidebar-logo"
                style={{ '--ct-custom-logo': `url("${logoUrl}")` }}
                role="img"
                aria-label={siteName}
              />
              <span
                className="ct-custom-theme-logo ct-custom-collapsed-logo"
                style={{ '--ct-custom-logo': `url("${faviconUrl}")` }}
                role="img"
                aria-label={`${siteName} favicon`}
              />
            </> : <>
              <div className="ct-logo-mark"><LogoMark /></div>
              <div className="ct-logo-text">
                <div className="ct-logo-title">{siteName}</div>
                <div className="ct-logo-sub">Central Reporting</div>
              </div>
            </>}
          </button>
        </div>

        <nav className="ct-nav">
          <NavLink
            to={dashboardPath}
            end
            className={({ isActive }) => 'ct-nav-item ct-mobile-dashboard-nav' + (isActive ? ' active' : '')}
            onClick={() => setMobileOpen(false)}
            title="Dashboard"
          >
            <span className="ct-nav-ico"><Ico name="dashboard" /></span>
            <span className="ct-nav-label">Dashboard</span>
          </NavLink>
          {NAV.map((sec) => {
            if (sec.roles && !allowed(sec.roles)) return null
            const items = sec.items.filter((n) => allowed(n.roles))
            if (!items.length) return null
            return (
              <div key={sec.section} className="ct-nav-group">
                <div className="ct-section-title">{sec.section}</div>
                {items.map((n) => {
                  const navTo = n.label === 'Dashboard' ? dashboardPath : n.to
                  return <NavLink
                    key={navTo} to={navTo} end={n.end}
                    className={({ isActive }) => 'ct-nav-item' + (n.label === 'Dashboard' ? ' ct-dashboard-nav' : '') + (isActive ? ' active' : '')}
                    onMouseEnter={(e) => showTip(e, n.label)}
                    onMouseLeave={hideTip}
                    title={n.label}
                  >
                    <span className="ct-nav-ico"><Ico name={n.icon} /></span>
                    <span className="ct-nav-label">{n.label}</span>
                  </NavLink>
                })}
              </div>
            )
          })}
        </nav>

        <div className="ct-side-foot" ref={footRef}>
          <div className="ct-account-row">
            <button
              className="ct-account ct-account-profile"
              onClick={() => navigate('/profile')}
              onMouseEnter={(e) => showTip(e, user?.name || 'Profile')}
              onMouseLeave={hideTip}
              title="Open profile"
            >
              <div className="ct-avatar">{initials(user?.name)}</div>
              <div className="ct-account-info">
                <div className="ct-account-name">{user?.name || 'User'}</div>
                <div className="ct-account-role">{user?.role || ''}</div>
              </div>
            </button>
            <button className="ct-account-logout" onClick={logout} title="Sign out" aria-label="Sign out">
              <Ico name="logout" size={17} />
            </button>
          </div>
        </div>
      </aside>

      {/* ════════════ MAIN ════════════ */}
      <div className="ct-main">
        <header className="ct-topbar">
          <button className="ct-topbar-burger" onClick={() => setMobileOpen((v) => !v)} aria-label="Toggle navigation">
            <Ico name="menu" size={20} />
          </button>

          <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--text)', whiteSpace: 'nowrap', marginRight: 6 }}>
            {pageTitle}
          </div>

          <div className="ct-topbar-actions" />

          <div className="ct-topbar-right">
            <TimezoneSelect />
            <button
              className="ct-icon-btn"
              onClick={cyclePreset}
              aria-label={isUser ? 'Apply next theme' : 'Toggle theme'}
              title={isUser ? `Next theme${preset ? ` · ${preset.index + 1} / ${preset.total}` : ''}` : (theme === 'dark' ? 'Light mode' : 'Dark mode')}
            >
              <Ico name={theme === 'dark' ? 'sun' : 'moon'} size={18} />
            </button>
          </div>
        </header>

        <main className="ct-page">
          {children}
        </main>
      </div>
    </div>
    </>
  )
}
